<script lang="ts">
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';
	import type { SbImage, SbMuxVideo, SbOverlay } from '$lib/storyblok/types';
	import Blok from '$lib/storyblok/blok.svelte';
	import { editable } from '$lib/storyblok/editable';
	import PageGrid16 from '../pageGrid16.svelte';
	import SbRichtext from '../sbRichtext.svelte';
	import Image from '../image.svelte';
	import Button from '$lib/buttons/v3/button.svelte';
	import FullscreenVideo from '$lib/fullscreenVideo.svelte';
	import Play from '$lib/icons/play.svelte';
	import { device } from '$lib/state/device.svelte';
	import { innerWidth } from 'svelte/reactivity/window';

	interface Props {
		content: ISbStoryData<{
			headline: ISbRichtext;
			headline_style: 'bold' | 'regular';
			subheadline: ISbRichtext;
			buttons: ISbStoryData[];
			text_alignment_mobile:
				| 'left-center'
				| 'left-bottom'
				| 'center-center'
				| 'center-bottom'
				| 'right-center'
				| 'right-bottom';
			text_alignment_desktop: 'left-center' | 'left-bottom' | 'center-center' | 'center-bottom';
			background_asset_mobile: ISbStoryData[];
			background_asset_desktop: ISbStoryData[];
			fullscreen_video?: ISbStoryData['content'];
			fullscreen_button_label?: string;
			headline_asset_mobile?: SbImage;
			headline_asset_mobile_size?: 'span-2' | 'span-4';
			headline_asset_desktop?: SbImage;
			headline_asset_desktop_position?: 'center' | 'left' | 'right';
			headline_asset_desktop_size?: 'span-4' | 'span-6' | 'span-8' | 'span-10';
			overlay_desktop: SbOverlay;
			overlay_mobile: SbOverlay;
			video_teaser_mobile?: ISbStoryData<SbMuxVideo>[];
			video_teaser_desktop?: ISbStoryData<SbMuxVideo>[];
		}>['content'];
	}

	let { content }: Props = $props();

	let asset = $derived(
		device.data.isDesktop
			? content.background_asset_desktop?.[0] || content.background_asset_mobile?.[0]
			: content.background_asset_mobile?.[0]
	);

	let teaserVideoBlock = $derived(
		device.data.isDesktop
			? content.video_teaser_desktop?.[0] || content.video_teaser_mobile?.[0]
			: content.video_teaser_mobile?.[0]
	);

	let fullscreenVideoPlaybackId = $derived(
		device.data.isDesktop
			? content.fullscreen_video?.[0]?.asset_desktop?.[0]?.playback_id ||
					content.fullscreen_video?.[0]?.asset_mobile?.[0]?.playback_id
			: content.fullscreen_video?.[0]?.asset_mobile?.[0]?.playback_id
	);

	let headlineAsset = $derived(
		device.data.isDesktop
			? content.headline_asset_desktop || content.headline_asset_mobile
			: content.headline_asset_mobile
	);

	function getColStartToCenter(span: string) {
		switch (span) {
			case 'span-4':
				return 7;
			case 'span-6':
				return 6;
			case 'span-8':
				return 5;
			case 'span-10':
				return 4;
			default:
				return 1;
		}
	}

	let expandedVideo = $state(false);

	let isMobile = $derived((innerWidth.current || 0) < 1024);
</script>

{#snippet buttons()}
	{#each content.buttons as btn}
		<Blok content={btn} />
	{/each}

	{#if fullscreenVideoPlaybackId}
		<Button class="btn-large btn-blur" onclick={() => (expandedVideo = true)}
			><span class="-ml-2 rounded-full bg-black/[0.12] p-2 backdrop-blur-[14px]">
				<Play class="h-6 w-6 translate-x-[1px] text-stone-5" />
			</span>
			<span class="pr-4">
				{content.fullscreen_button_label}
			</span>
		</Button>
	{/if}
{/snippet}

<section id="hero" class="relative text-white" use:editable={content}>
	{#if asset}
		<div class="absolute inset-0">
			<Blok content={asset} />
		</div>
		<div
			class="absolute inset-0"
			style="background-color: rgba(0, 0, 0, 0.{device.data.isDesktop
				? content.overlay_desktop || 0
				: content.overlay_mobile || 0});"
		></div>
	{/if}
	{#if headlineAsset?.filename}
		<div class={['flex h-full flex-col justify-center']}>
			<PageGrid16 margin>
				<div
					class={[
						`col-${content.headline_asset_mobile_size} lg:col-${content.headline_asset_desktop_size} z-10`,

						content.headline_asset_desktop_position === 'center' &&
							`lg:col-start-${getColStartToCenter(content.headline_asset_desktop_size || '')}`,

						{
							'lg:col-end-[16]': content.headline_asset_desktop_position === 'right'
						},
						{
							'col-start-2': content.headline_asset_mobile_size === 'span-2'
						}
					]}
				>
					<Image
						class="w-full"
						src={headlineAsset.filename}
						alt={headlineAsset.alt}
						title={headlineAsset.title}
					/>
					{#if content.subheadline}
						<div
							class="mt-3 text-pretty text-center text-[20px]/[140%] tracking-[-.2px] lg:mt-4 lg:text-[24px]/[140%] lg:tracking-[-.24px]"
						>
							<SbRichtext text={content.subheadline} />
						</div>
					{/if}
				</div>
				<div class="col-span-full mt-6 flex justify-center gap-x-2 lg:mt-9">
					{@render buttons()}
				</div>
			</PageGrid16>
		</div>
	{:else}
		<div
			class={[
				'flex h-full flex-col',
				{
					'max-lg:justify-center': ['left-center', 'center-center'].includes(
						content.text_alignment_mobile
					)
				},
				{
					'max-lg:text-center': ['center-bottom', 'center-center'].includes(
						content.text_alignment_mobile
					)
				},
				{
					'max-lg:justify-end max-lg:pb-12':
						['left-bottom', 'center-bottom', 'right-bottom'].includes(
							content.text_alignment_mobile
						) && !teaserVideoBlock
				},
				{
					'lg:justify-center': ['left-center', 'center-center', 'right-center'].includes(
						content.text_alignment_desktop
					)
				},
				{
					'lg:text-center': ['center-bottom', 'center-center'].includes(
						content.text_alignment_desktop
					)
				},
				{
					'lg:justify-end lg:pb-[88px]':
						['left-bottom', 'center-bottom', 'right-bottom'].includes(
							content.text_alignment_desktop
						) && !teaserVideoBlock
				},
				teaserVideoBlock && 'justify-end max-lg:pb-5 lg:pb-[64px]'
			]}
		>
			<PageGrid16 margin>
				<div
					class={[
						'z-10 col-span-full ',
						{
							'lg:col-span-6 lg:col-start-2 max-lg:landscape:col-end-4': [
								'left-center',
								'left-bottom'
							].includes(content.text_alignment_desktop)
						},
						{
							'lg:col-span-6 lg:col-start-10': ['right-center', 'right-bottom'].includes(
								content.text_alignment_desktop
							)
						},
						{
							'lg:col-span-12 lg:col-start-3': ['center-center', 'center-bottom'].includes(
								content.text_alignment_desktop
							)
						}
					]}
				>
					<div
						class={[
							'text-pretty text-[52px]/[100%] tracking-[-.52px] lg:text-[80px]/[100%] lg:tracking-[-1.6px]',
							{
								'font-semibold ': content.headline_style === 'bold'
							}
						]}
					>
						<SbRichtext text={content.headline} />
					</div>

					{#if content.subheadline}
						<div
							class="mt-3 text-pretty text-[20px]/[140%] tracking-[-.2px] lg:mt-5 lg:text-[20px]/[140%] lg:tracking-[-.20px]"
						>
							<SbRichtext text={content.subheadline} />
						</div>
					{/if}
					<div
						class={[
							'mt-6 flex gap-x-2 lg:mt-9',
							{
								'max-lg:justify-center': ['center-center', 'center-bottom'].includes(
									content.text_alignment_mobile
								)
							},
							{
								'lg:justify-center': ['center-center', 'center-bottom'].includes(
									content.text_alignment_desktop
								)
							}
						]}
					>
						{@render buttons()}
					</div>
				</div>

				{#if teaserVideoBlock}
					<div
						class="z-10 col-span-full flex flex-col justify-end max-lg:mt-7 lg:col-start-12 lg:col-end-[17] max-lg:landscape:col-start-4"
					>
						<div
							class="relative aspect-3/2 overflow-hidden rounded-xl max-lg:min-w-[196px] max-lg:max-w-[50%]"
						>
							<Blok content={teaserVideoBlock} />
							<div
								class="absolute inset-0 flex items-center justify-center"
								style="background-color: rgba(0, 0, 0, 0.12);"
							>
								<Button class={['btn-white', isMobile && 'btn-small']}><Play /></Button>
							</div>
						</div>
					</div>
				{/if}
			</PageGrid16>
		</div>
	{/if}
</section>

{#if expandedVideo}
	<FullscreenVideo
		backgroundWithOpacity
		buttonPos="top-right"
		playbackId={fullscreenVideoPlaybackId}
		onclose={() => (expandedVideo = false)}
	/>
{/if}

<style lang="scss">
	section {
		height: calc(100svh - var(--banner-height));
	}
</style>
